import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const POINTER_TRANSIT_DELAY = 100;
const DEACTIVATE_DELAY = 350;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // On the document, not on this element. A panel opened by hover never took
    // focus — it is still on whatever the user last clicked, usually <body> —
    // and a listener here only ever hears keys pressed inside the menu, so
    // Escape reached it in every case except the commonest one. The Escape
    // branch is guarded on there being an open panel and the trigger branch on
    // `this.contains`, so nothing else on the page is affected.
    document.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unlatch();
    this.#pointerTransitDeactivate.cancel();
    this.#debouncedDeactivate.cancel();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * The trigger whose panel has been latched open by a click, if any.
   *
   * A latched panel deliberately survives `pointerleave` and `blur` — it closes
   * only on Escape, on focus leaving the header menu, or on a click outside it.
   * This holds the trigger anchor rather than a boolean so it compares directly
   * against `#state.activeItem`, which `findMenuItem` resolves to the same node.
   *
   * @type {HTMLElement | null}
   */
  #latchedItem = null;

  /**
   * Signals the listeners that exist only while a panel is latched, so they are
   * torn down in one `abort()` instead of being carried on every page.
   *
   * @type {AbortController | null}
   */
  #latchController = null;

  /**
   * Whether the latch was opened by a pointer rather than the keyboard.
   *
   * Escape has to put focus back on the trigger — the panel it came from is
   * `visibility: hidden` once closed, so focus would otherwise fall to <body>
   * and the next Tab would restart at the top of the page. But Escape is itself
   * a key press, so the browser's focus-visible heuristic treats that restore
   * as keyboard-driven and draws the ring, even for someone who never left the
   * mouse. This is what lets #restoreFocus tell those two apart.
   *
   * @type {boolean}
   */
  #latchedByPointer = false;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#pointerTransitDeactivate.cancel();
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#pointerTransitDeactivate.cancel();
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    // Hovering a different top-level item opens that one and drops the old
    // latch, so a panel latched by a click cannot outlive the item it belongs
    // to. Cleared before the early return below, which a re-activation of the
    // already-active item takes.
    if (this.#latchedItem && item !== this.#latchedItem) this.#unlatch();

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item when it loses the pointer or focus.
   *
   * Blur remains immediate. Pointer movement gets a short transit window because
   * the top-level item occupies the menu content box while its absolutely
   * positioned panel starts below the padded header row. Crossing that visual
   * gap can briefly fire pointerleave even though the pointer is moving into the
   * panel. Re-entering the item or any descendant cancels the pending close.
   *
   * This is deliberately separate from DEACTIVATE_DELAY: the overflow panel is
   * outside its trigger and needs the longer 350ms grace period, while ordinary
   * panels need only enough time to cross the header padding.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item !== this.#state.activeItem) return;

    // `blur` is captured at the document and resolved with closest(), so this
    // fires for anything focusable inside the item — the panel's own links
    // included. Moving focus from the trigger into the panel is not leaving the
    // item, and closing on it would put the panel out of keyboard reach.
    if (event.type === 'blur') {
      const next = /** @type {FocusEvent} */ (event).relatedTarget;

      if (next instanceof Node && event.target.contains(next)) return;
      this.#deactivate();
      return;
    }

    this.#pointerTransitDeactivate(item);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (item.matches(':hover') || this.overflowHovered) return;

    // The whole latch, in one place. Every legitimate closer — Escape, focus
    // leaving the header, a click outside — calls #unlatch() first, so this
    // covers `on:pointerleave`, `on:blur` and the overflow menu's debounced
    // path without a separate check at each of those three call sites.
    if (item === this.#latchedItem) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /** Short grace period for crossing from an L1 item into its panel. */
  #pointerTransitDeactivate = debounce(this.#deactivate, POINTER_TRANSIT_DELAY);

  /** Longer grace period for the overflow panel, which is outside its trigger. */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Clicking a top-level item latches its panel open and moves focus into it.
   *
   * The five items this can match are exactly the ones with a mega panel:
   * blocks/_header-menu.liquid emits `aria-haspopup="true"` only when
   * `link.links != blank`. A link inside the panel cannot match, because the
   * panel is a sibling `<div>` of the trigger rather than a child of it, so
   * closest() from a panel link never walks through the trigger.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.menu-list__link[aria-haspopup="true"]');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    // These five never navigate on desktop; the click opens the panel instead.
    // Production is the same shape — every tier-one item on ocs.ca is href="#",
    // which menu-link-target renders as role="button" with no href at all.
    // Deliberately no stopPropagation: a CTA click listener further out still
    // needs to see this.
    event.preventDefault();

    // Synchronous activation, not this.activate(). That routes through
    // debounce(handler, 0), which is still a setTimeout and so has not applied
    // `aria-expanded` by the time we want to focus — and the handler no-ops
    // outright when the item is already active, which it is after a hover.
    // Calling it directly also means its `submenu.offsetHeight` read has forced
    // a layout, flushing the style recalc that turns the panel's
    // `visibility: hidden` into `visible`. A hidden element cannot take focus,
    // so that flush is what makes the focus() below land.
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#activateHandler({ target: trigger.closest('.menu-list__list-item') || trigger });

    const target = findFirstFocusable(findSubmenu(trigger));

    // A panel with nothing to tab to would be a latch the keyboard cannot get
    // out of by any route but Escape. Leave focus on the trigger instead.
    if (!target) return;

    this.#latch(trigger);
    this.#latchedByPointer = true;
    target.focus();
  };

  /**
   * Hold a panel open until Escape, focus leaving the menu, or a click outside.
   *
   * @param {HTMLElement} trigger
   */
  #latch = (trigger) => {
    this.#unlatch();

    this.#latchedItem = trigger;
    this.dataset.latched = '';
    this.#latchController = new AbortController();

    const { signal } = this.#latchController;

    // pointerdown rather than click, so the panel is out of the way before the
    // click resolves against whatever is underneath it.
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.#closeLatched();
      },
      { signal }
    );

    // Tabbing past the last link in the panel. Shift+Tab off the first one
    // lands on the trigger, which is inside `this`, so that keeps it open.
    this.addEventListener(
      'focusout',
      (event) => {
        const next = /** @type {FocusEvent} */ (event).relatedTarget;
        if (next instanceof Node && this.contains(next)) return;
        this.#closeLatched();
      },
      { signal }
    );
  };

  /**
   * Release the latch without closing the panel — the panel may be about to be
   * replaced by another one rather than dismissed.
   */
  #unlatch = () => {
    this.#latchController?.abort();
    this.#latchController = null;
    this.#latchedItem = null;
    this.#latchedByPointer = false;
    delete this.dataset.latched;
  };

  /**
   * Put focus back on the trigger, without the focus ring when the panel was
   * opened by a pointer.
   *
   * `focus({ focusVisible: false })` is the purpose-built API for this, but
   * only Firefox honours it, so the ring is suppressed in CSS instead. The
   * attribute is cleared the moment focus leaves the trigger, so tabbing on
   * from here — or shift-tabbing back to it — draws the ring as normal. A
   * keyboard user who opened the panel with Space keeps it throughout.
   *
   * @param {HTMLElement} trigger
   * @param {boolean} quiet
   */
  #restoreFocus = (trigger, quiet) => {
    if (!quiet) {
      trigger.focus();
      return;
    }

    trigger.dataset.focusQuiet = '';
    trigger.addEventListener('blur', () => delete trigger.dataset.focusQuiet, { once: true });
    trigger.focus({ focusVisible: false });
  };

  /**
   * Release the latch and close the panel it was holding open.
   */
  #closeLatched = () => {
    const item = this.#latchedItem;
    this.#unlatch();
    this.#deactivate(item ?? undefined);
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.key === 'Escape') {
      const activeItem = this.#state.activeItem;
      if (!activeItem) return;
      event.preventDefault();
      // Read before #unlatch clears it.
      const quiet = this.#latchedByPointer;
      // Whether there is any focus to put back. A hover-opened panel never took
      // focus, so restoring it would move a mouse user's focus into the header
      // from wherever they actually left it.
      const held = this.contains(document.activeElement);
      // Before #deactivate, whose latch guard would otherwise refuse this.
      this.#unlatch();
      this.#deactivate(activeItem);
      if (held) this.#restoreFocus(activeItem, quiet);
      return;
    }

    const trigger = event.target.closest('.menu-list__link[aria-haspopup="true"]');
    if (!trigger || !this.contains(trigger)) return;

    // Enter as well as Space. The trigger is an anchor carrying role="button"
    // and, being a placeholder, no href — so there is nothing for the UA to
    // activate on Enter and it did nothing at all. Both keys operate a button.
    if (event.key !== ' ' && event.key !== 'Enter') return;

    event.preventDefault();
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.#deactivate(trigger);
    } else {
      this.activate({ target: trigger.closest('.menu-list__list-item') || trigger });
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * The first element inside a panel that can take focus.
 *
 * `getFocusableElements` in assets/focus.js is not exported, and its companion
 * `trapFocus` is the wrong shape here — Tab has to be able to leave the panel,
 * which is what closes a latched one. Same selector idiom as
 * assets/layered-slideshow.js instead.
 *
 * @param {HTMLElement | null} container
 * @returns {HTMLElement | null}
 */
function findFirstFocusable(container) {
  if (!container) return null;

  for (const element of container.querySelectorAll(FOCUSABLE_SELECTOR)) {
    // A panel renders every column at once and hides the ones that are not in
    // play, so presence in the DOM is not enough to be focusable.
    if (element instanceof HTMLElement && element.getClientRects().length > 0) return element;
  }

  return null;
}

// TEMPORARY DEBUG HELPER: remove this block after desktop mega menu debugging is complete.
if (typeof window !== 'undefined') {
  window.openMenu = openDebugMenu;
  window.closeMenu = closeDebugMenu;
  window.closeOpenMenu = closeDebugMenu;
}

function openDebugMenu(label) {
  const normalizedLabel = normalizeDebugMenuLabel(label);

  if (!normalizedLabel) {
    console.warn('Usage: window.openMenu("Featured")');
    return null;
  }

  const headerMenu = findDebugHeaderMenu();

  if (!headerMenu) {
    console.warn('No desktop <header-menu> element found.');
    return null;
  }

  const menuItem = findDebugMenuItem(headerMenu, normalizedLabel);

  if (!menuItem) {
    console.warn(`No desktop mega menu item matched "${label}".`);
    return null;
  }

  // Delegate to the real activation path so debug-opening is identical to
  // genuine pointerenter/focus hover (same aria/dataset state, same
  // content-visibility flip, same offsetHeight-based --submenu-height calc).
  headerMenu.activate({ target: menuItem });

  return menuItem;
}

function closeDebugMenu() {
  const headerMenu = findDebugHeaderMenu();

  if (!headerMenu) return false;

  const activeLink = headerMenu.querySelector('[ref="menuitem"][aria-expanded="true"]');

  if (!activeLink) return false;

  const menuItem = activeLink.closest('.menu-list__list-item');

  if (!menuItem) return false;

  // Delegate to the real deactivation path (same debounce/animation timing
  // as a genuine pointerleave) instead of hand-rolling a close.
  headerMenu.deactivate({ target: menuItem });

  return true;
}

function findDebugHeaderMenu() {
  let fallback = null;

  for (const headerMenu of document.querySelectorAll('header-menu')) {
    if (!(headerMenu instanceof HTMLElement)) continue;

    fallback ??= headerMenu;

    if (headerMenu.offsetParent !== null) return headerMenu;
  }

  return fallback;
}

function findDebugMenuItem(headerMenu, normalizedLabel) {
  let partialMatch = null;

  for (const menuItem of headerMenu.querySelectorAll('.menu-list__list-item')) {
    if (!(menuItem instanceof HTMLElement)) continue;

    const menuLink = findDebugMenuLink(menuItem);
    const itemLabel = normalizeDebugMenuLabel(menuItem.dataset.menuTitle || menuLink?.textContent || '');

    if (!itemLabel || !menuLink || !findSubmenu(menuLink)) continue;
    if (itemLabel === normalizedLabel) return menuItem;
    if (!partialMatch && itemLabel.includes(normalizedLabel)) partialMatch = menuItem;
  }

  return partialMatch;
}

function findDebugMenuLink(menuItem) {
  const menuLink = menuItem.querySelector(':scope > .menu-list__link, :scope > [ref="menuitem"]');
  return menuLink instanceof HTMLElement ? menuLink : null;
}

function normalizeDebugMenuLabel(label) {
  return String(label ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}
